/**
 * StreamingWriter — writes test data to disk incrementally.
 *
 * v3 format: each test gets a directory with separate files:
 *   tests/<testId>/meta.json        — lightweight metadata
 *   tests/<testId>/network.jsonl    — network requests (moved from temp)
 *   tests/<testId>/console.jsonl    — console logs (moved from temp)
 *   tests/<testId>/api-calls.jsonl  — API calls (moved from temp)
 *
 * Heavy data files are moved (renamed) from the worker's temp directory,
 * never loaded into the reporter's memory.
 */

import { createHash } from 'node:crypto';
import { mkdirSync, writeFileSync, renameSync, unlinkSync, copyFileSync } from 'node:fs';
import { join } from 'node:path';
import type {
  TestIndexEntry,
  TestDetailData,
  StreamingReportSummary,
  StreamingReportManifest,
  StreamingWriteError,
} from '@testrelic/core';

const REPORT_DIR_NAME = '.testrelic-report';

/** Generate a deterministic test ID from test identity fields. */
export function generateStreamingTestId(
  filePath: string,
  titlePath: readonly string[],
  project: string,
  retryIndex: number,
): string {
  const input = `${filePath}|${titlePath.join('|')}|${project}|${retryIndex}`;
  return createHash('sha256').update(input).digest('hex').substring(0, 12);
}

/**
 * Generate a key shared by every attempt of the same test, so retries can be
 * correlated with their final attempt without re-sending the full titlePath.
 * Same inputs as {@link generateStreamingTestId} minus `retryIndex`.
 */
export function generateRetryGroupKey(
  filePath: string,
  titlePath: readonly string[],
  project: string,
): string {
  const input = `${filePath}|${titlePath.join('|')}|${project}`;
  return createHash('sha256').update(input).digest('hex').substring(0, 12);
}

export class StreamingWriter {
  private readonly reportDir: string;
  private readonly testsDir: string;
  private readonly writeErrors: StreamingWriteError[] = [];
  private totalBytesWritten = 0;

  constructor(outputDir: string) {
    this.reportDir = join(outputDir, REPORT_DIR_NAME);
    this.testsDir = join(this.reportDir, 'tests');
    mkdirSync(this.testsDir, { recursive: true });
  }

  /** Get the report directory path. */
  getReportDir(): string {
    return this.reportDir;
  }

  /** Get accumulated write errors. */
  getWriteErrors(): readonly StreamingWriteError[] {
    return this.writeErrors;
  }

  /**
   * Write a single test's detail data to disk.
   *
   * Creates a per-test directory containing:
   * - meta.json: lightweight metadata (navigations, assertions, actions, artifacts, failure)
   * - network.jsonl: moved from temp (if provided)
   * - console.jsonl: moved from temp (if provided)
   * - api-calls.jsonl: moved from temp (if provided)
   *
   * @param testId — Deterministic test ID
   * @param data — Metadata (inline small data)
   * @param files — Paths to JSONL files to move into the test directory
   */
  writeTestDetail(
    testId: string,
    data: TestDetailData,
    files?: {
      networkRequestsFile?: string | null;
      consoleLogsFile?: string | null;
      apiCallsFile?: string | null;
    },
  ): boolean {
    try {
      const testDir = join(this.testsDir, testId);
      mkdirSync(testDir, { recursive: true });

      // Write metadata
      const metaPath = join(testDir, 'meta.json');
      const tmpPath = metaPath + '.tmp';
      const json = JSON.stringify(data);
      writeFileSync(tmpPath, json, 'utf-8');
      renameSync(tmpPath, metaPath);
      this.totalBytesWritten += Buffer.byteLength(json, 'utf-8');

      // Move JSONL data files from temp to test directory
      if (files?.networkRequestsFile) {
        this.moveFile(files.networkRequestsFile, join(testDir, 'network.jsonl'));
      }
      if (files?.consoleLogsFile) {
        this.moveFile(files.consoleLogsFile, join(testDir, 'console.jsonl'));
      }
      if (files?.apiCallsFile) {
        this.moveFile(files.apiCallsFile, join(testDir, 'api-calls.jsonl'));
      }

      return true;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      this.writeErrors.push({
        testId,
        error: errorMsg,
        timestamp: new Date().toISOString(),
      });
      process.stderr.write(
        `[testrelic] Failed to write test detail for ${testId}: ${errorMsg}\n`,
      );
      return false;
    }
  }

  /**
   * Move a file from source to destination.
   * Falls back to copy+delete if rename fails (cross-device move).
   */
  private moveFile(src: string, dest: string): void {
    try {
      renameSync(src, dest);
    } catch {
      // Cross-device: copy then delete.
      // copyFileSync is imported statically rather than required here. This was
      // a lazy require, but it bought nothing — node:fs is already imported at
      // the top of this file — and it broke the fallback in the ESM build: tsup
      // emits a `require` shim that throws "Dynamic require of fs is not
      // supported", which the catch below swallowed into a "Failed to move
      // file" message. So a cross-device move never actually fell back.
      try {
        copyFileSync(src, dest);
        try { unlinkSync(src); } catch { /* best effort cleanup */ }
      } catch (err) {
        process.stderr.write(
          `[testrelic] Failed to move file ${src} → ${dest}: ${err instanceof Error ? err.message : String(err)}\n`,
        );
      }
    }
  }

  /** Write the test index to disk. */
  writeIndex(entries: readonly TestIndexEntry[]): void {
    const filePath = join(this.reportDir, 'index.json');
    const tmpPath = filePath + '.tmp';
    const json = JSON.stringify(entries);
    writeFileSync(tmpPath, json, 'utf-8');
    renameSync(tmpPath, filePath);
    this.totalBytesWritten += Buffer.byteLength(json, 'utf-8');
  }

  /**
   * Write a compact index for HTML embedding (strips titlePath/project to save ~100 bytes/entry).
   */
  writeCompactIndex(entries: readonly TestIndexEntry[]): void {
    const filePath = join(this.reportDir, 'index-compact.json');
    const tmpPath = filePath + '.tmp';
    const compact = entries.map(({ titlePath: _tp, project: _p, ...rest }) => rest);
    const json = JSON.stringify(compact);
    writeFileSync(tmpPath, json, 'utf-8');
    renameSync(tmpPath, filePath);
    this.totalBytesWritten += Buffer.byteLength(json, 'utf-8');
  }

  /** Write the summary to disk. */
  writeSummary(summary: StreamingReportSummary): void {
    const filePath = join(this.reportDir, 'summary.json');
    const tmpPath = filePath + '.tmp';
    const json = JSON.stringify(summary);
    writeFileSync(tmpPath, json, 'utf-8');
    renameSync(tmpPath, filePath);
    this.totalBytesWritten += Buffer.byteLength(json, 'utf-8');
  }

  /** Write the manifest to disk. */
  writeManifest(manifest: StreamingReportManifest): void {
    const filePath = join(this.reportDir, 'manifest.json');
    const tmpPath = filePath + '.tmp';
    const json = JSON.stringify(manifest);
    writeFileSync(tmpPath, json, 'utf-8');
    renameSync(tmpPath, filePath);
    this.totalBytesWritten += Buffer.byteLength(json, 'utf-8');
  }

  /** Get total bytes written (tracked incrementally — no filesystem scan). */
  computeTotalSize(): number {
    return this.totalBytesWritten;
  }

  /** Cleanup — no-op for now, but available for future resource release. */
  dispose(): void {
    // Reserved for future cleanup
  }
}
